// One class: its details and roster. School admins add and remove members (one at a time
// or by CSV), record consent and edit or archive the class; teachers see the roster
// read-only. Below it: assignments, review, the class's progress report and its reports,
// and (for its teachers and school admins) who the class is shared with. The server
// decides who may see what — this page only hides controls the viewer could not use.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Upload, UserPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { createLogger } from '../../utils/logger';
import LoadingIndicator from '../loading-indicator/LoadingIndicator';
import ConfirmationModal from '../shared/ConfirmationModal';
import {
  ClassInput,
  ClassMember,
  ClassRole,
  Roster,
  RosterImportResult,
  SchoolClass,
  addClassMember,
  fetchClass,
  fetchRoster,
  importClassRoster,
  recordStudentConsent,
  removeClassMember,
  revokeStudentConsent,
  updateClass,
} from '../../services/classService';
import ClassAssignmentsSection from './ClassAssignmentsSection';
import ClassProgressSection from './ClassProgressSection';
import ClassReportsSection from './ClassReportsSection';
import ClassReviewSection from './ClassReviewSection';
import ClassSharingSection from './ClassSharingSection';
import ClassFormModal from './ClassFormModal';
import {
  cardStyle,
  dangerLinkStyle,
  errorMessage,
  errorStyle,
  headerRowStyle,
  hintStyle,
  inputStyle,
  labelStyle,
  linkButtonStyle,
  noticeStyle,
  pageStyle,
  primaryButtonStyle,
  roleBadgeStyle,
  secondaryButtonStyle,
  sectionHeadingStyle,
  statusBadgeStyle,
  subtitleStyle,
  tableStyle,
  tdStyle,
  thStyle,
  titleStyle,
} from './classesStyles';

const logger = createLogger('ClassDetailPage');
const ROLES: ClassRole[] = ['student', 'teacher', 'co_teacher'];

const ClassDetailPage: React.FC = () => {
  const { classId = '' } = useParams<{ classId: string }>();
  const { t } = useTranslation();
  const { userRole } = useAuth();
  const isSchoolAdmin = userRole === 'admin' || userRole === 'super_admin';

  const [schoolClass, setSchoolClass] = useState<SchoolClass | null>(null);
  const [roster, setRoster] = useState<Roster | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [removing, setRemoving] = useState<ClassMember | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ClassRole>('student');
  const [importResult, setImportResult] = useState<RosterImportResult | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const loaded = await fetchClass(classId);
      setSchoolClass(loaded);
      // A student may open their own class but not its roster.
      if (loaded.my_role === 'student' && !isSchoolAdmin) {
        setRoster(null);
      } else {
        setRoster(await fetchRoster(classId));
      }
    } catch (err) {
      logger.error('Failed to load class', err);
      const status = (err as { status?: number }).status;
      setLoadError(status === 403 || status === 404 ? t('classes.noAccess') : t('classes.couldNotLoadClass'));
    } finally {
      setLoading(false);
    }
  }, [classId, isSchoolAdmin, t]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (work: () => Promise<unknown>, success: string | null, fallback: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await work();
      if (success) setNotice(success);
      await load();
    } catch (err) {
      logger.error(fallback, err);
      setError(errorMessage(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <LoadingIndicator message={t('classes.loading')} />;
  if (loadError || !schoolClass) {
    return (
      <div style={pageStyle}>
        <div style={errorStyle} role="alert">
          {loadError ?? t('classes.couldNotLoadClass')}
        </div>
        <Link to={isSchoolAdmin ? '/classes' : '/my-classes'}>{t('classes.backToClasses')}</Link>
      </div>
    );
  }

  const active = schoolClass.status === 'active';
  const canManage = isSchoolAdmin;
  const canChangeRoster = canManage && active;
  // Teaching (assigning practice) belongs to the class's teachers and its school admins;
  // a grantee who can see the roster cannot assign.
  const teaches = isSchoolAdmin || schoolClass.my_role === 'teacher' || schoolClass.my_role === 'co_teacher';
  const meta = [schoolClass.subject, schoolClass.grade_range, schoolClass.school_year].filter(Boolean).join(' · ');

  const addMember = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    run(
      async () => {
        await addClassMember(classId, email.trim(), role);
        setEmail('');
      },
      t('classes.memberAdded'),
      t('classes.couldNotAddMember')
    );
  };

  const upload = (file: File | undefined) => {
    if (!file) return;
    setImportResult(null);
    run(
      async () => setImportResult(await importClassRoster(classId, file)),
      null,
      t('classes.couldNotImport')
    ).finally(() => {
      if (fileInput.current) fileInput.current.value = '';
    });
  };

  const saveClass = async (input: ClassInput) => {
    const updated = await updateClass(classId, input);
    setSchoolClass(updated);
    setEditing(false);
    setNotice(t('classes.classSaved'));
  };

  const consentCell = (m: ClassMember) => {
    if (m.role !== 'student' || !roster?.consent_required) return '—';
    return (
      <span style={statusBadgeStyle(Boolean(m.has_consent))}>
        {m.has_consent ? t('classes.consentGiven') : t('classes.consentMissing')}
      </span>
    );
  };

  const memberName = (m: ClassMember) => m.display_name || m.email || m.user_id;

  return (
    <div style={pageStyle}>
      <Link
        to={isSchoolAdmin ? '/classes' : '/my-classes'}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.85rem', marginBottom: '0.75rem' }}
      >
        <ArrowLeft size={14} /> {t('classes.backToClasses')}
      </Link>

      <div style={headerRowStyle}>
        <div>
          <h1 id="tour-page-title" style={titleStyle}>
            {schoolClass.name}{' '}
            <span style={{ ...statusBadgeStyle(active), verticalAlign: 'middle' }}>
              {t(`classes.statuses.${schoolClass.status}`)}
            </span>
          </h1>
          {meta && <p style={subtitleStyle}>{meta}</p>}
        </div>
        {canManage && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" style={secondaryButtonStyle} onClick={() => setEditing(true)} disabled={busy}>
              {t('classes.editClass')}
            </button>
            <button
              type="button"
              style={secondaryButtonStyle}
              disabled={busy}
              onClick={() =>
                active
                  ? setConfirmArchive(true)
                  : run(() => updateClass(classId, { status: 'active' }), t('classes.classRestored'), t('classes.couldNotSaveClass'))
              }
            >
              {active ? t('classes.archive') : t('classes.restore')}
            </button>
          </div>
        )}
      </div>

      {error && <div style={errorStyle} role="alert">{error}</div>}
      {notice && <div style={noticeStyle} role="status">{notice}</div>}

      {schoolClass.rolled_over_to_id && (
        <div style={noticeStyle} role="note">
          {t('studentAdmin.rollover.rolledOverNotice')}{' '}
          <Link to={`/classes/${schoolClass.rolled_over_to_id}`}>{t('studentAdmin.rollover.openNewClass')}</Link>
        </div>
      )}

      {canChangeRoster && (
        <section style={cardStyle}>
          <h2 style={sectionHeadingStyle}>{t('classes.addMembers')}</h2>
          <form onSubmit={addMember} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 240px' }}>
              <label htmlFor="member-email" style={labelStyle}>
                {t('common.email')}
              </label>
              <input
                id="member-email"
                type="email"
                style={{ ...inputStyle, width: '100%' }}
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={t('classes.emailPlaceholder')}
              />
            </div>
            <div>
              <label htmlFor="member-role" style={labelStyle}>
                {t('common.role')}
              </label>
              <select id="member-role" style={inputStyle} value={role} onChange={e => setRole(e.target.value as ClassRole)}>
                {ROLES.map(r => (
                  <option key={r} value={r}>
                    {t(`classes.roles.${r}`)}
                  </option>
                ))}
              </select>
            </div>
            <button type="submit" style={primaryButtonStyle} disabled={busy || !email.trim()}>
              <UserPlus size={16} /> {t('classes.add')}
            </button>
          </form>
          <p style={hintStyle}>{t('classes.addMembersHint')}</p>

          <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <input
              ref={fileInput}
              id="roster-csv"
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={e => upload(e.target.files?.[0])}
            />
            <button type="button" style={secondaryButtonStyle} disabled={busy} onClick={() => fileInput.current?.click()}>
              <Upload size={16} /> {t('classes.uploadCsv')}
            </button>
            <span style={{ ...hintStyle, margin: 0 }}>{t('classes.csvHint')}</span>
          </div>

          {importResult && (
            <div style={{ marginTop: '1rem' }} aria-live="polite">
              <div style={importResult.skipped ? errorStyle : noticeStyle} role="status">
                {t('classes.importSummary', {
                  added: importResult.added + importResult.reactivated,
                  updated: importResult.updated,
                  unchanged: importResult.unchanged,
                  skipped: importResult.skipped,
                })}
              </div>
              {importResult.skipped > 0 && (
                <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.82rem', color: '#991b1b' }}>
                  {importResult.rows
                    .filter(r => r.outcome === 'skipped')
                    .map(r => (
                      <li key={r.row}>
                        {t('classes.importSkippedRow', { row: r.row, email: r.email || '—', reason: r.reason })}
                      </li>
                    ))}
                </ul>
              )}
            </div>
          )}
        </section>
      )}

      {roster ? (
        <section style={cardStyle}>
          <h2 style={sectionHeadingStyle}>
            {t('classes.roster')} ({roster.members.length})
          </h2>
          {roster.consent_required && (
            <p style={{ ...hintStyle, marginTop: 0, marginBottom: '0.75rem' }}>
              {t('classes.rosterConsentHint', { type: t(`classes.consentTypes.${roster.consent_type}`) })}
            </p>
          )}
          {roster.members.length === 0 ? (
            <p style={hintStyle}>{t('classes.noMembers')}</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>{t('common.name')}</th>
                    <th style={thStyle}>{t('common.email')}</th>
                    <th style={thStyle}>{t('common.role')}</th>
                    <th style={thStyle}>{t('classes.consent')}</th>
                    {canManage && <th style={thStyle}>{t('common.actions')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {roster.members.map(m => (
                    <tr key={m.user_id}>
                      <td style={tdStyle}>{memberName(m)}</td>
                      <td style={tdStyle}>{m.email || '—'}</td>
                      <td style={tdStyle}>
                        <span style={roleBadgeStyle(m.role)}>{t(`classes.roles.${m.role}`)}</span>
                      </td>
                      <td style={tdStyle}>{consentCell(m)}</td>
                      {canManage && (
                        <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                          {/* A school that wants students' own consent gets no Record button:
                              that record would read as the student's agreement. */}
                          {m.role === 'student' &&
                            roster.consent_required &&
                            (m.has_consent || roster.consent_type !== 'student') && (
                            <button
                              type="button"
                              style={{ ...linkButtonStyle, marginRight: 12 }}
                              disabled={busy}
                              aria-label={
                                m.has_consent
                                  ? t('classes.revokeConsentFor', { name: memberName(m) })
                                  : t('classes.recordConsentFor', { name: memberName(m) })
                              }
                              onClick={() =>
                                m.has_consent
                                  ? run(
                                      () => revokeStudentConsent(classId, m.user_id),
                                      t('classes.consentRevoked'),
                                      t('classes.couldNotSaveConsent')
                                    )
                                  : run(
                                      () => recordStudentConsent(classId, m.user_id),
                                      t('classes.consentRecorded'),
                                      t('classes.couldNotSaveConsent')
                                    )
                              }
                            >
                              {m.has_consent ? t('classes.revokeConsent') : t('classes.recordConsent')}
                            </button>
                          )}
                          <button
                            type="button"
                            style={dangerLinkStyle}
                            disabled={busy}
                            aria-label={t('classes.removeMember', { name: memberName(m) })}
                            onClick={() => setRemoving(m)}
                          >
                            {t('common.remove')}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : (
        <section style={cardStyle}>
          <p style={{ ...hintStyle, margin: 0 }}>{t('classes.studentClassHint')}</p>
        </section>
      )}

      {roster && <ClassAssignmentsSection classId={classId} canAssign={teaches && active} />}

      {roster && <ClassReviewSection classId={classId} />}

      {roster && <ClassProgressSection classId={classId} />}

      {roster && <ClassReportsSection classId={classId} canResolve={teaches} />}

      {roster && teaches && <ClassSharingSection classId={classId} active={active} />}

      {editing && <ClassFormModal initial={schoolClass} onSubmit={saveClass} onClose={() => setEditing(false)} />}

      <ConfirmationModal
        isOpen={confirmArchive}
        title={t('classes.archiveTitle')}
        message={t('classes.archiveMessage', { name: schoolClass.name })}
        confirmText={t('classes.archive')}
        loading={busy}
        onCancel={() => setConfirmArchive(false)}
        onConfirm={() => {
          setConfirmArchive(false);
          run(() => updateClass(classId, { status: 'archived' }), t('classes.classArchived'), t('classes.couldNotSaveClass'));
        }}
      />

      <ConfirmationModal
        isOpen={removing !== null}
        title={t('classes.removeTitle')}
        message={removing ? t('classes.removeMessage', { name: memberName(removing), className: schoolClass.name }) : ''}
        confirmText={t('common.remove')}
        loading={busy}
        onCancel={() => setRemoving(null)}
        onConfirm={() => {
          const member = removing;
          setRemoving(null);
          if (member) {
            run(() => removeClassMember(classId, member.user_id), t('classes.memberRemoved'), t('classes.couldNotRemoveMember'));
          }
        }}
      />
    </div>
  );
};

export default ClassDetailPage;
